import React, { useState, useRef, useCallback, useId } from "react";
import { Button } from "../../ui/UIElements/Button";
import { Icon } from "../../ui/UIElements/Icon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../ui/UIElements/Popover";

/**
 * Custom brand (franchise) selector with infinite scroll in the dropdown list.
 * Replaces a native <select> so scroll position can trigger loading the next page.
 */
const FranchiseSelect = ({
  value,
  onChange,
  disabled = false,
  placeholder,
  placeholderLoading,
  loadingMoreLabel,
  franchises = [],
  isLoading = false,
  isFetchingNextPage = false,
  hasNextPage = false,
  fetchNextPage,
  disabledSuffix = "",
  required = false,
  id,
  "aria-label": ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef(null);
  const optionRefs = useRef([]);
  const typeaheadRef = useRef("");
  const typeaheadTimeoutRef = useRef(null);
  const generatedId = useId();
  const controlId = id || `franchise-select-${generatedId.replace(/:/g, "")}`;
  const listboxId = `${controlId}-listbox`;

  const selectedFranchise = franchises.find(
    (f) => String(f.id) === String(value),
  );
  const displayLabel = selectedFranchise
    ? `${selectedFranchise.name}${!selectedFranchise.enabled ? ` ${disabledSuffix}` : ""}`
    : "";

  const moreBrandsLoadingText = loadingMoreLabel ?? placeholderLoading ?? "";

  const enabledIndexes = franchises.reduce((indexes, franchise, index) => {
    if (franchise.enabled) indexes.push(index);
    return indexes;
  }, []);

  const focusOption = useCallback((index) => {
    setActiveIndex(index);
    requestAnimationFrame(() => optionRefs.current[index]?.focus());
  }, []);

  const handleOpenChange = (nextOpen) => {
    if (disabled && nextOpen) return;
    setIsOpen(nextOpen);
    if (nextOpen) {
      const selectedIndex = franchises.findIndex(
        (franchise) =>
          franchise.enabled && String(franchise.id) === String(value),
      );
      setActiveIndex(
        selectedIndex >= 0 ? selectedIndex : (enabledIndexes[0] ?? -1),
      );
    }
  };

  const handleScroll = useCallback(() => {
    if (!listRef.current || isFetchingNextPage || !hasNextPage) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      fetchNextPage?.();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  const handleSelectFranchise = (franchise) => {
    if (!franchise.enabled) return;
    onChange(String(franchise.id));
    setIsOpen(false);
  };

  const handleTriggerKeyDown = (event) => {
    if (["ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      const nextIndex =
        event.key === "ArrowUp"
          ? (enabledIndexes.at(-1) ?? -1)
          : (enabledIndexes[0] ?? -1);
      setActiveIndex(nextIndex);
      setIsOpen(true);
    }
  };

  const handleOptionKeyDown = (event, currentIndex) => {
    const position = enabledIndexes.indexOf(currentIndex);
    let nextIndex = null;

    if (event.key === "ArrowDown") {
      nextIndex = enabledIndexes[(position + 1) % enabledIndexes.length];
    } else if (event.key === "ArrowUp") {
      nextIndex =
        enabledIndexes[
          (position - 1 + enabledIndexes.length) % enabledIndexes.length
        ];
    } else if (event.key === "Home") {
      nextIndex = enabledIndexes[0];
    } else if (event.key === "End") {
      nextIndex = enabledIndexes.at(-1);
    }

    if (nextIndex != null) {
      event.preventDefault();
      focusOption(nextIndex);
      return;
    }

    if (
      event.key.length === 1 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey
    ) {
      typeaheadRef.current += event.key.toLocaleLowerCase();
      window.clearTimeout(typeaheadTimeoutRef.current);
      typeaheadTimeoutRef.current = window.setTimeout(() => {
        typeaheadRef.current = "";
      }, 500);
      const matchIndex = franchises.findIndex(
        (franchise) =>
          franchise.enabled &&
          franchise.name?.toLocaleLowerCase().startsWith(typeaheadRef.current),
      );
      if (matchIndex >= 0) focusOption(matchIndex);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          id={controlId}
          variant="outline"
          size="touch"
          disabled={disabled}
          onKeyDown={handleTriggerKeyDown}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-required={required}
          aria-label={ariaLabel}
          aria-busy={isLoading || isFetchingNextPage}
          className="custom-select w-full min-w-0 justify-between text-start"
        >
          <span
            className={`min-w-0 flex-1 truncate ${
              value && displayLabel
                ? "text-foreground"
                : "text-muted-foreground"
            }`}
          >
            {isLoading && !franchises.length
              ? placeholderLoading
              : displayLabel || placeholder}
          </span>
          {isFetchingNextPage ? (
            <Icon
              name="Loader2"
              className="shrink-0 animate-spin motion-reduce:animate-none"
            />
          ) : (
            <Icon name="ChevronDown" className="shrink-0" />
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        id={listboxId}
        role="listbox"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabel ? undefined : controlId}
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          if (activeIndex >= 0) focusOption(activeIndex);
        }}
        onCloseAutoFocus={() => {
          window.clearTimeout(typeaheadTimeoutRef.current);
          typeaheadRef.current = "";
        }}
        className="w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-24px)] p-0"
      >
        <div
          ref={listRef}
          className="max-h-48 overflow-y-auto py-1"
          onScroll={handleScroll}
        >
          {isLoading && franchises.length === 0 ? (
            <div className="text-muted-foreground flex items-center justify-center gap-2 px-3 py-4 text-sm">
              <Icon
                name="Loader2"
                className="animate-spin motion-reduce:animate-none"
              />
              {placeholderLoading}
            </div>
          ) : (
            franchises.map((franchise, index) => {
              const isSelected = String(franchise.id) === String(value);
              const itemDisabled = !franchise.enabled;
              return itemDisabled ? (
                <div
                  key={franchise.id}
                  role="option"
                  aria-disabled="true"
                  aria-selected="false"
                  className="text-muted-foreground cursor-not-allowed px-3 py-2 text-sm opacity-60"
                >
                  {franchise.name} {disabledSuffix}
                </div>
              ) : (
                <Button
                  key={franchise.id}
                  ref={(node) => {
                    optionRefs.current[index] = node;
                  }}
                  type="button"
                  role="option"
                  tabIndex={activeIndex === index ? 0 : -1}
                  aria-selected={isSelected}
                  emphasis="ghost"
                  intent="neutral"
                  onFocus={() => setActiveIndex(index)}
                  onKeyDown={(event) => handleOptionKeyDown(event, index)}
                  onClick={() => handleSelectFranchise(franchise)}
                  className="h-auto min-h-9 w-full justify-start rounded-none px-3 py-2 text-start"
                >
                  {isSelected ? (
                    <Icon name="Check" className="text-primary shrink-0" />
                  ) : (
                    <span className="w-4 shrink-0" aria-hidden="true" />
                  )}
                  <span>{franchise.name}</span>
                </Button>
              );
            })
          )}
          {isFetchingNextPage ? (
            <div
              className="text-muted-foreground flex items-center justify-center gap-2 px-3 py-2"
              aria-live="polite"
            >
              <Icon
                name="Loader2"
                className="shrink-0 animate-spin motion-reduce:animate-none"
              />
              <span className="text-xs">{moreBrandsLoadingText}</span>
            </div>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default FranchiseSelect;
